import {
  faMoonStars,
  faSunBright,
  faBed,
  faAlarmClock,
  faBowlSpoon,
  faUtensils,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useEffect } from 'react';

import '../styles/componentStyles/CircularSlider.css';

import { useTranslation } from 'react-i18next';

import Cursor from './Cursor';

const outerWidth = 475;
const outerStroke = 75;
const stroke = 2.5;
const radius = 200;
const { PI } = Math;
const centre = { x: outerWidth / 2, y: outerWidth / 2 };
const TAU = 2 * PI;
const sweepFlag = 1;

function CircularSlider(props) {
  const { t } = useTranslation();

  const arc = (x, y, largeArcFlag) => {
    return `A ${radius} ${radius} 0 ${largeArcFlag ? 1 : 0} ${sweepFlag} ${x} ${y}`;
  };

  const convertToPI = (value) => {
    const xPos = value.x - centre.x;
    const yPos = value.y - centre.y;
    const angle = Math.atan2(yPos, xPos);
    const PIValue = normalize(angle);
    return PIValue;
  };

  const normalize = (angle) => {
    const rest = angle % TAU;
    return rest > 0 ? rest : TAU + rest;
  };

  const angleToTime = (angle) => {
    const duration = Math.round((24 * 60 * angle) / TAU);
    const minutes = duration % 60;
    const hours = Math.floor(duration / 60) % 24;

    const formattedHours = hours.toString().padStart(2, '0');
    const formattedMinutes = minutes.toString().padStart(2, '0');

    return { hours: formattedHours, minutes: formattedMinutes };
  };

  const timeToAngle = (time) => {
    const [hours, minutes] = time.split(':').map(Number);

    const totalMinutes = hours * 60 + minutes;
    const angle = (totalMinutes * 2) / (24 * 60);

    return angle * PI;
  };

  const absoluteDuration = (start, end) => (start > end ? end + (TAU - start) : end - start);

  const [isEndPressed, setIsEndPressed] = useState(false);
  const [isStartPressed, setIsStartPressed] = useState(false);
  const [isSliderPressed, setIsSliderPressed] = useState(false);
  const [startAngle, setStartAngle] = useState(false);
  const [sliderCursor, setSliderCursor] = useState('grab');
  const [startSliderCursor, setStartSliderCursor] = useState('grab');
  const [endSliderCursor, setEndSliderCursor] = useState('grab');
  const [start, setStart] = useState(false);
  const [end, setEnd] = useState(false);
  const [startTime, setStartTime] = useState(false);
  const [endPos, setEndPos] = useState(false);
  const [endTime, setEndTime] = useState(false);
  const [startPos, setStartPos] = useState(false);

  useEffect(() => {
    if (props.startTime) {
      setStart(() => timeToAngle(props.startTime));
    }
  }, [props.startTime]);

  useEffect(() => {
    if (start) {
      setStartTime(() => angleToTime(start));
      setStartPos(() => ({
        x: centre.x + radius * Math.cos(start),
        y: centre.y + radius * Math.sin(start),
      }));
    }
  }, [start]);

  useEffect(() => {
    if (props.endTime) {
      setEnd(() => timeToAngle(props.endTime));
    }
  }, [props.endTime]);

  useEffect(() => {
    if (end) {
      setEndTime(() => angleToTime(end));
      setEndPos(() => ({
        x: centre.x + radius * Math.cos(end),
        y: centre.y + radius * Math.sin(end),
      }));
    }
  }, [end]);

  const handleStartTimeChange = (e) => {
    const newTime = e.target.value;
    const [hours, minutes] = newTime.split(':');
    const formattedTime = `${hours}:${minutes}`;

    setStartTime({ hours, minutes });

    const newStart = timeToAngle(formattedTime);
    setStart(newStart);

    props.saveResponse('data', {
      id: props.questionid[0],
      value: formattedTime,
    });
  };

  const handleEndTimeChange = (e) => {
    const newTime = e.target.value;
    const [hours, minutes] = newTime.split(':');
    const formattedTime = `${hours}:${minutes}`;

    setEndTime({ hours, minutes });

    const newEnd = timeToAngle(formattedTime);
    setEnd(newEnd);

    props.saveResponse('data', {
      id: props.questionid[1],
      value: formattedTime,
    });
  };

  const animatedProps = React.useMemo(() => {
    const p1 = startPos;
    const p2 = endPos;
    const startPI = convertToPI(startPos);
    const endPI = convertToPI(endPos);
    const duration = absoluteDuration(startPI, endPI);
    return {
      d: `M ${p1.x} ${p1.y} ${arc(p2.x, p2.y, duration > PI)}`,
    };
  }, [startPos, endPos]);

  const handleMouseMove = (event) => {
    if (isEndPressed || isStartPressed || (isSliderPressed && startAngle)) {
      // check if mobile browser is used
      if (event.type === 'touchmove') {
        event.preventDefault(); // Prevent the default behavior
        event = event.touches[0];
      }
      const { clientX, clientY } = event;
      const slider = document.getElementById('slider');
      const rect = slider.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const angle = Math.atan2(clientY - centerY, clientX - centerX) * (180 / Math.PI);

      if (isEndPressed) {
        const newEnd = (((angle + 90) * 2) / 360) * PI;
        const newEndTime = angleToTime(normalize(newEnd));
        setEndTime(newEndTime);
        props.saveResponse('data', {
          id: props.questionid[1],
          value: `${newEndTime.hours}:${newEndTime.minutes}`,
        });
        setEndPos({
          x: centre.x + radius * Math.cos(newEnd),
          y: centre.y + radius * Math.sin(newEnd),
        });
      } else if (isStartPressed) {
        const newStart = (((angle + 90) * 2) / 360) * PI;
        const newStartTime = angleToTime(normalize(newStart));
        setStartTime(newStartTime);
        props.saveResponse('data', {
          id: props.questionid[0],
          value: `${newStartTime.hours}:${newStartTime.minutes}`,
        });
        setStartPos({
          x: centre.x + radius * Math.cos(newStart),
          y: centre.y + radius * Math.sin(newStart),
        });
      } else if (isSliderPressed && startAngle) {
        const travelledAngle = angle - startAngle;
        const oldEnd = convertToPI(endPos);
        const newEnd = oldEnd + ((travelledAngle * 2) / 360) * PI;
        const newEndTime = angleToTime(normalize(newEnd));
        setEndTime(newEndTime);
        props.saveResponse('data', {
          id: props.questionid[1],
          value: `${newEndTime.hours}:${newEndTime.minutes}`,
        });
        setEndPos({
          x: centre.x + radius * Math.cos(newEnd),
          y: centre.y + radius * Math.sin(newEnd),
        });
        const oldStart = convertToPI(startPos);
        const newStart = oldStart + ((travelledAngle * 2) / 360) * Math.PI;
        const newStartTime = angleToTime(normalize(newStart));
        setStartTime(newStartTime);
        props.saveResponse('data', {
          id: props.questionid[1],
          value: `${newStartTime.hours}:${newStartTime.minutes}`,
        });
        setStartPos({
          x: centre.x + radius * Math.cos(newStart),
          y: centre.y + radius * Math.sin(newStart),
        });
      }
    }
  };

  const handleMouseUp = () => {
    setIsEndPressed(false);
    setIsStartPressed(false);
    setIsSliderPressed(false);
    setStartAngle(false);
    setSliderCursor('grab');
    setStartSliderCursor('grab');
    setEndSliderCursor('grab');
    const slider = document.getElementById('slider');
    slider.removeEventListener('mousemove', handleMouseMove);
    slider.removeEventListener('touchmove', handleMouseMove);

    const endEl = document.getElementById('end');
    endEl.removeEventListener('mousemove', handleMouseMove);
    endEl.removeEventListener('touchmove', handleMouseMove);

    const startEl = document.getElementById('start');
    startEl.removeEventListener('mouseup', handleMouseMove);
    startEl.removeEventListener('touchmove', handleMouseMove);
  };

  useEffect(() => {
    const endEl = document.getElementById('end');

    if (isEndPressed && endEl) {
      endEl.addEventListener('mousemove', handleMouseMove);
      endEl.addEventListener('touchmove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchend', handleMouseUp);

      return () => {
        endEl.removeEventListener('mousemove', handleMouseMove);
        endEl.removeEventListener('touchmove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchend', handleMouseUp);
      };
    }
  }, [isEndPressed]);

  useEffect(() => {
    const startEl = document.getElementById('start');

    if (isStartPressed && startEl) {
      startEl.addEventListener('mousemove', handleMouseMove);
      startEl.addEventListener('touchmove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchend', handleMouseUp);

      return () => {
        startEl.removeEventListener('mousemove', handleMouseMove);
        startEl.removeEventListener('touchmove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchend', handleMouseUp);
      };
    }
  }, [isStartPressed]);

  useEffect(() => {
    const slider = document.getElementById('slider');

    if (isSliderPressed && slider && startAngle) {
      slider.addEventListener('mousemove', handleMouseMove);
      slider.addEventListener('touchmove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchend', handleMouseUp);

      return () => {
        slider.removeEventListener('mousemove', handleMouseMove);
        slider.removeEventListener('touchmove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchend', handleMouseUp);
      };
    }
  }, [isSliderPressed, startAngle]);

  return (
    <div className="circular-slider">
      {startTime && endTime && (
        <div className="slider-labels">
          <fieldset className="circular-slider-column">
            <legend
              className="circular-slider-label sr-only"
              aria-label={`${props.title} ${props.meals ? t('T058') : t('T060')}`}
            />
            <FontAwesomeIcon icon={props.meals ? faBowlSpoon : faBed} className="icon " />
            <p className="circular-slider-label" aria-hidden="true">
              {props.meals ? t('T058') : t('T060')}
            </p>
            <div className="circular-slider-input-group">
              <div className="time-input-container">
                <label htmlFor="start-hours-input" className="sr-only">
                  {t('trackers.circularSlider.startHours', {
                    type: props.meals
                      ? t('trackers.circularSlider.meal')
                      : t('trackers.circularSlider.sleep'),
                  })}
                </label>
                <input
                  id="start-hours-input"
                  className="time-number-input"
                  type="number"
                  min="0"
                  max="23"
                  value={startTime.hours}
                  onChange={(e) => {
                    const hours = Math.min(23, Math.max(0, parseInt(e.target.value, 10) || 0));
                    const newTime = `${hours.toString().padStart(2, '0')}:${startTime.minutes}`;
                    handleStartTimeChange({ target: { value: newTime } });
                  }}
                />
                <span aria-hidden="true">:</span>
                <label htmlFor="start-minutes-input" className="sr-only">
                  {t('trackers.circularSlider.startMinutes', {
                    type: props.meals
                      ? t('trackers.circularSlider.meal')
                      : t('trackers.circularSlider.sleep'),
                  })}
                </label>
                <input
                  id="start-minutes-input"
                  className="time-number-input"
                  type="number"
                  min="0"
                  max="59"
                  value={startTime.minutes}
                  onChange={(e) => {
                    const minutes = Math.min(59, Math.max(0, parseInt(e.target.value, 10) || 0));
                    const newTime = `${startTime.hours}:${minutes.toString().padStart(2, '0')}`;
                    handleStartTimeChange({ target: { value: newTime } });
                  }}
                />
              </div>
            </div>
          </fieldset>
          <fieldset className="circular-slider-column">
            <legend
              className="circular-slider-label sr-only"
              aria-label={`${props.title} ${props.meals ? t('T059') : t('T061')}`}
            />
            <FontAwesomeIcon icon={props.meals ? faUtensils : faAlarmClock} className="icon " />
            <p className="circular-slider-label" aria-hidden="true">
              {props.meals ? t('T059') : t('T061')}
            </p>
            <div className="circular-slider-input-group">
              <div className="time-input-container">
                <label htmlFor="end-hours-input" className="sr-only">
                  {t('trackers.circularSlider.endHours')}
                </label>
                <input
                  id="end-hours-input"
                  className="time-number-input"
                  type="number"
                  min="0"
                  max="23"
                  value={endTime.hours}
                  onChange={(e) => {
                    const hours = Math.min(23, Math.max(0, parseInt(e.target.value, 10) || 0));
                    const newTime = `${hours.toString().padStart(2, '0')}:${endTime.minutes}`;
                    handleEndTimeChange({ target: { value: newTime } });
                  }}
                />
                <span aria-hidden="true">:</span>
                <label htmlFor="end-minutes-input" className="sr-only">
                  {t('trackers.circularSlider.endMinutes')}
                </label>
                <input
                  id="end-minutes-input"
                  className="time-number-input"
                  type="number"
                  min="0"
                  max="59"
                  value={endTime.minutes}
                  onChange={(e) => {
                    const minutes = Math.min(59, Math.max(0, parseInt(e.target.value, 10) || 0));
                    const newTime = `${endTime.hours}:${minutes.toString().padStart(2, '0')}`;
                    handleEndTimeChange({ target: { value: newTime } });
                  }}
                />
              </div>
            </div>
          </fieldset>
        </div>
      )}
      {startPos && endPos && (
        <div className="fs-exclude" aria-hidden="true">
          <svg className="circular-slider-container" width={outerWidth} height={outerWidth}>
            <g>
              <g>
                <circle
                  className="outer-circle"
                  cx="50%"
                  cy="50%"
                  r={(outerWidth - outerStroke) / 2}
                  stroke="#f1ebf4"
                  strokeWidth={outerStroke}
                  fill="#fff"
                  style={{
                    stroke: 'var(--color-background-faded-dark)',
                    strokeWidth: `${outerStroke}`,
                    fill: '#fff',
                    userSelect: 'none',
                  }}
                />
                {[...Array(24).keys()]
                  .filter((num) => num % 2 === 0)
                  .map((item, i) => {
                    const delta = TAU / 12;
                    const theta = delta * i - PI / 2;
                    const p1 = {
                      x: centre.x + (radius - 60) * Math.cos(theta),
                      y: centre.y + (radius - 60) * Math.sin(theta),
                    };

                    return (
                      <React.Fragment key={i}>
                        <g transform={`translate(${p1.x - 5}, ${p1.y + 5})`}>
                          <text
                            x={0}
                            y={0}
                            fontWeight="bold"
                            fontSize={item % 6 === 0 ? 14 : 12}
                            fill={item % 6 === 0 ? '#281D2D' : 'var(--color-background-faded-dark)'}
                            fontFamily="FuturaPT-Medium"
                            style={{
                              WebkitUserSelect: 'none',
                              MozUserSelect: 'none',
                              MsUserSelect: 'none',
                              userSelect: 'none',
                            }}
                          >
                            {item}
                          </text>
                          {item === 0 && (
                            <foreignObject
                              x={-10}
                              y={5}
                              width={35}
                              height={30}
                              style={{
                                textAlign: 'center',
                                color: '#ddcedb',
                                userSelect: 'none',
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  width: '100%',
                                  height: '100%',
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={faMoonStars}
                                  style={{
                                    fontSize: '30px',
                                    color: '#41c0d3',
                                  }}
                                />
                              </div>
                            </foreignObject>
                          )}
                          {item === 12 && (
                            <foreignObject
                              x={-10}
                              y={-45}
                              width={35}
                              height={30}
                              style={{
                                textAlign: 'center',
                                color: '#ddcedb',
                                userSelect: 'none',
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  width: '100%',
                                  height: '100%',
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={faSunBright}
                                  style={{
                                    fontSize: '30px',
                                    color: '#fcc954',
                                  }}
                                />
                              </div>
                            </foreignObject>
                          )}
                        </g>
                      </React.Fragment>
                    );
                  })}
              </g>
              <circle
                className="track"
                cx="50%"
                cy="50%"
                r={radius}
                stroke="#fff"
                strokeWidth={stroke}
                fill="transparent"
              />
              <defs>
                <mask id="mask">
                  <path
                    stroke="#fff"
                    strokeWidth={stroke}
                    {...animatedProps}
                    transform={`translate(${centre.x}, ${
                      centre.y
                    }) rotate(-90) translate(${-centre.x}, ${-centre.y})`}
                  />
                </mask>
              </defs>
              <svg id="slider" mask="url(#mask)">
                <circle
                  cx={centre.x}
                  cy={centre.y}
                  r={radius + stroke / 2}
                  fill="#e92364"
                  style={{ cursor: 'default' }}
                />
              </svg>
              <Cursor
                pos={startPos}
                cx={centre.x}
                cy={centre.y}
                sliderCursor={startSliderCursor}
                region="start"
                handleStartMouseDown={() => {}}
                meals={props.meals}
              />
              <Cursor
                pos={endPos}
                region="end"
                cx={centre.x}
                cy={centre.y}
                sliderCursor={endSliderCursor}
                handleEndMouseDown={() => {}}
                meals={props.meals}
              />
            </g>
          </svg>
        </div>
      )}
    </div>
  );
}

export default CircularSlider;
